
import React from 'react';
import I18n from 'i18n-js';
import gql from 'graphql-tag';
import { ChildProps } from 'react-apollo';

import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import CmsListingsCollection from '@reverbdotcom/commons/src/cms/components/cms_listings_collection';
import { convertMoney } from '@reverbdotcom/commons/src/convert_currency';
import { Paths } from '@reverbdotcom/commons/src/url_helpers';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { parseAmountCents } from '@reverbdotcom/commons/src/money';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import experiments from '@reverbdotcom/commons/src/experiments';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { experimentInt } from '@reverbdotcom/commons/src/user_context_helpers';
import { CATEGORY_UNDER_PRICE_CONDITIONS } from '@reverbdotcom/commons/src/condition_helpers';

import { CURRENCY_CODES } from '../../../shared/constants';
import { productTypesAndFilters } from './category_under_price_helper';
import { categoryForUuid } from '../category_data';
import {
  CategoryUnderPriceQuery,
  reverb_search_ListingsSearchRequest_Sort,
} from '../../../schemas/rql';

export const ELECTRIC_GUITAR_UUID = 'dfd39027-d134-4353-b9e4-57dc6be791b9';

type Props = ChildProps<{}, CategoryUnderPriceQuery.Query>;

export function CategoryUnderPrice({ data }: Props) {

  const { me, exchangeRates } = data;

  const hasAffinityProductType = me?.profile?.topProductTypeUuids?.length;
  const affinityProductTypeUuids = me?.profile?.topProductTypeUuids;
  const affinityProductTypeUuid = hasAffinityProductType ? affinityProductTypeUuids[0] : null;
  const affinityProductTypeSlug = hasAffinityProductType ? categoryForUuid(affinityProductTypeUuid).slug : null;
  const filtersExistForAffinity = !!(hasAffinityProductType && productTypesAndFilters[affinityProductTypeUuid]);
  // use electric guitar as default if user affinity category does not exist in our price map
  const topProductTypeUuid = filtersExistForAffinity ? affinityProductTypeUuid : ELECTRIC_GUITAR_UUID;
  const topProductTypeFilters = productTypesAndFilters[topProductTypeUuid];

  const { priceMinUsd, priceGreatMaxUsdCents, priceGoodMaxUsdCents, brandFilters } = topProductTypeFilters;
  let priceMaxUsd, priceMax, priceMin;

  const experiment = experiments.CATEGORY_UNDER_PRICE_LO_HP;
  const user = useUser();
  const expGroup = experimentInt(user, experiment);

  if (expGroup === 0) priceMaxUsd = 0;
  if (expGroup === 1) priceMaxUsd = priceGreatMaxUsdCents;
  if (expGroup === 2) priceMaxUsd = priceGoodMaxUsdCents;

  try {
    priceMax = convertMoney({ amountCents: priceMaxUsd, currency: CURRENCY_CODES.USD }, user.currency, exchangeRates.rates, { precision: 0 });
    priceMin = priceMinUsd && convertMoney({ amountCents: priceMinUsd, currency: CURRENCY_CODES.USD }, user.currency, exchangeRates.rates, { precision: 0 });
  } catch {
    // an error is thrown if currency
    // is not found in exchangeRates
    // use usd as fallback
    priceMax = parseAmountCents(priceMaxUsd, CURRENCY_CODES.USD, { precision: 0 });
    priceMin = priceMinUsd && parseAmountCents(priceMinUsd, CURRENCY_CODES.USD, { precision: 0 });
  }

  useViewTracking({
    eventName: MParticleEventName.ComponentView,
    componentName: 'CategoryUnderPrice',
    experiments: formatExperimentsForEventAttributes(user, [experiment]),
    affinityProductTypeSlug,
    shownProductTypeSlug: topProductTypeFilters.slug,
    shownDefaultProductType: !filtersExistForAffinity,
    priceMax: priceMax.amount,
    region: user.shippingRegionCode,
    countryCode: user.countryCode,
    currency: user.currency,
  }, true);

  const productTypeName = I18n.t(`discovery.taxonomy.${topProductTypeUuid}`);

  const ctaTargetHref = Paths.marketplace.expand({
    sort: 'published_at|desc',
    product_type: topProductTypeFilters.slug,
    price_max: priceMax.amount,
    price_min: priceMin && priceMin.amount,
    make: brandFilters,
    condition: CATEGORY_UNDER_PRICE_CONDITIONS,
    exclude_local_pickup_only: true,
  });

  if (expGroup <= 0) return null;

  return (
    <CmsListingsCollection
      title={I18n.t('discovery.homepage.categoryUnderPrice.title', {
        productType: productTypeName,
        price: priceMax.display,
      })}
      componentName="HomepageLoggedOutCategoryUnderPrice"
      trackingName="HomepageLoggedOutCategoryUnderPrice"
      ctaTargetHref={ctaTargetHref}
      categoryUuids={[topProductTypeUuid]}
      brandSlugs={brandFilters}
      conditionSlugs={CATEGORY_UNDER_PRICE_CONDITIONS}
      sort={reverb_search_ListingsSearchRequest_Sort.PUBLISHED_AT_DESC}
      priceMax={priceMax.amount}
      priceMin={priceMin && priceMin.amount}
      currency={priceMax.currency}
      localPickup={false}
      maxCount={12}
      largeTiles
      ssr={false}
    />
  );
}

const withQuery = withGraphql<Props, CategoryUnderPriceQuery.Query>(
  gql`
    query Category_Under_Price_Query {
      me {
        uuid
        profile {
          topProductTypeUuids
        }
      }
      exchangeRates(input: { inverseReverseRates: true }) {
        rates {
          from
          to
          rate
        }
      }
    }
  `,
);

export default withQuery(CategoryUnderPrice);
